/**
 * App-wide active-license infrastructure. This directory holds no route view.
 *
 * `useActiveLicense` registers one React Query entry per store, polled once a minute and shared
 * by every consumer that needs the license. Do not wrap it behind another abstraction or add a
 * second license query: a different cache key means extra requests against the same endpoint and
 * two copies of the status that drift apart. The README next to this file lists the consumers.
 */
import { useEffect } from 'react';
import { useAuth } from 'hooks/useAuth';
import { useQuery } from 'react-query';
import { useLocation } from 'react-router';
import {
  getUserCurrentStore,
  removeAuthToken,
  removeCookieUser,
  removeOrganizationUser,
  removeStoreBackupKey,
  removeStoreDratfKey,
  removeUserCurrentStore,
  removeUserStoresStoreKey,
} from 'utils/authTyped';
import history from 'utils/history';

import { removeBrandSelectedLocalStorage, clearMenuScopeLocalStorage } from 'utils/menuMaker';

import { getActiveLicense } from './services';

const ONE_MINUTE = 60000;

const SUBSCRIPTION_PATH_PREFIX = '/subscriptions';
const ONBOARDING_PATH = '/onboarding';

export const ACTIVE_LICENSE_QUERY_KEY = 'active-license';

export function useActiveLicense() {
  const { isAuthenticated } = useAuth();
  const storeUuid = getUserCurrentStore()?.uuid;

  return useQuery({
    queryKey: [ACTIVE_LICENSE_QUERY_KEY, storeUuid],
    queryFn: () => getActiveLicense({ storeUuid }),
    refetchInterval: ONE_MINUTE,
    refetchIntervalInBackground: true,
    enabled: isAuthenticated,
    notifyOnChangeProps: ['data'],
    retry: 1,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      if (error.response && error.response.status === 401) {
        removeAuthToken();
        removeUserStoresStoreKey();
        removeUserCurrentStore();
        removeStoreDratfKey();
        removeStoreBackupKey();
        removeBrandSelectedLocalStorage();
        removeOrganizationUser();
        removeCookieUser();

        // Clear menu scope state
        clearMenuScopeLocalStorage();

        history.push({
          pathname: '/logged-out',
        });
      }
    },
  });
}

/**
 * Whether the license watcher is about to navigate away.
 *
 * It is exported so the account verification gate can stand behind it without re-deriving the
 * condition. Two copies of this rule would drift on the first change, and the symptom would be a
 * blocking dialog on top of the payment screen — the one place where the user can do nothing about
 * either.
 *
 * @param input.status - Status of the active license, absent while it is still loading
 * @param input.wizardCompleted - Whether the onboarding wizard was finished
 * @param input.isDraftStore - Whether the current store is a draft, which is exempt from onboarding
 * @returns True when a blocking post-login redirect is pending
 */
export const isLicenseRedirectPending = (input: {
  status?: string;
  wizardCompleted?: boolean;
  isDraftStore: boolean;
}): boolean => {
  const { status, wizardCompleted, isDraftStore } = input;

  if (!status) return false;
  if (status === 'UNAVAILABLE' || status === 'PAYMENT_REQUIRED') return true;

  return status === 'AVAILABLE' && wizardCompleted === false && !isDraftStore;
};

/**
 * Hook form of the predicate, reading the active license.
 *
 * It is what the account verification gate uses to wait its turn: subscription and onboarding both
 * win over it, because a dialog asking someone to verify their email on top of an expired-license
 * screen asks for something that unblocks nothing, and it covers the payment screen while it does.
 *
 * @returns True when the license watcher is about to navigate away
 */
export function useIsLicenseRedirectPending(): boolean {
  const { data } = useActiveLicense();
  const isDraftStore = getUserCurrentStore()?.isDraft ?? false;

  return isLicenseRedirectPending({
    status: data?.status,
    wizardCompleted: data?.wizardCompleted,
    isDraftStore,
  });
}

/**
 * Watches the active license status and redirects accordingly:
 * - Invalid license (UNAVAILABLE / PAYMENT_REQUIRED): redirects to /subscriptions.
 * - Available license but wizard not completed (and not a draft store):
 *   redirects to /onboarding.
 *
 * It decides **whether** to navigate with `isLicenseRedirectPending` and only then decides where.
 * That is the same predicate the account verification gate stands behind, so the watcher and the
 * gate cannot disagree by construction.
 *
 * The destination is a prefix check, not an equality one: the block creation wizard
 * lives under /subscriptions/blocks/create, and an invalid license must not expel the
 * user from it.
 */
export function useLicenseRedirect() {
  const { data } = useActiveLicense();
  const location = useLocation();
  const status = data?.status;
  const wizardCompleted = data?.wizardCompleted;
  const isDraftStore = getUserCurrentStore()?.isDraft ?? false;

  useEffect(() => {
    if (!isLicenseRedirectPending({ status, wizardCompleted, isDraftStore })) return;

    if (status === 'UNAVAILABLE' || status === 'PAYMENT_REQUIRED') {
      if (!location.pathname.startsWith(SUBSCRIPTION_PATH_PREFIX)) {
        history.push({ pathname: SUBSCRIPTION_PATH_PREFIX });
      }
      return;
    }

    // The predicate already ruled out every case that is not onboarding, so only the "already
    // there" check is left.
    if (location.pathname !== ONBOARDING_PATH) {
      history.push({ pathname: ONBOARDING_PATH });
    }
  }, [status, wizardCompleted, isDraftStore, location.pathname]);
}
